/**
 * La ficha de una conversación.  (US2, FR-015..FR-019)
 *
 * El corazón: la línea de tiempo honesta. Alrededor, el resultado (auditoría),
 * los mensajes en vivo de Chatwoot (en un sobre que degrada solo, FR-018) y los
 * enlaces. Nada de esto se guarda: los mensajes se piden en el momento.
 */
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'

import { ControlDelHilo } from '../components/ControlDelHilo'
import { MensajesConversacion } from '../components/MensajesConversacion'
import { OutcomeBadge } from '../components/OutcomeBadge'
import { Timeline } from '../components/Timeline'
import { Badge } from '../components/ui/badge'
import { api } from '../lib/api'
import { useRefrescoHiloAbierto } from '../lib/use-refresco-hilo'
import { fechaHora, hora } from '../lib/tiempo'
import type { DetalleConversacion, EventoAuditoriaVista } from '../lib/tipos'

export function ConversationDetail() {
    const { accountId, id } = useParams()
    /** La consola de ESTE negocio: es a donde se vuelve, no a la portada. */
    const consola = `/business/${accountId}/console`
    const { data, isPending, isError, refetch, isFetching } = useQuery({
        queryKey: ['conversation', id],
        queryFn: () => api.get<DetalleConversacion>(`/conversations/${id}`),
    })
    useRefrescoHiloAbierto(() => {
        void refetch()
    })

    if (isPending) return <Marco volverA={consola}><p className="text-sm text-neutral-500">Cargando…</p></Marco>
    if (isError || !data) return <Marco volverA={consola}><p className="text-sm text-red-400">No se encontró la conversación.</p></Marco>

    return (
        <Marco volverA={consola}>
            <Cabecera d={data} />
            <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
                <section>
                    <Titulo>Línea de tiempo</Titulo>
                    <Timeline entradas={data.timeline} />
                    {data.handovers.length > 0 && (
                        <div className="mt-4">
                            <Titulo>Escalados</Titulo>
                            {data.handovers.map((h, i) => (
                                <div key={i} className="flex items-center gap-2 py-1 text-sm">
                                    <Badge tone="danger">{h.reason}</Badge>
                                    {h.stateAtHandover && <span className="text-neutral-500">en {h.stateAtHandover}</span>}
                                    <span className="ml-auto font-mono text-xs text-neutral-600">{hora(h.at)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="mt-4">
                        <Titulo>Resultado</Titulo>
                        <Resultado audit={data.audit} />
                    </div>
                </section>
                <section>
                    <Titulo>Mensajes <span className="font-normal text-neutral-600">(en vivo, Chatwoot)</span></Titulo>
                    <MensajesConversacion
                        sobre={data.messages}
                        onReintentar={() => void refetch()}
                        recargando={isFetching}
                        idHilo={id ?? null}
                    />
                    {data.accountId !== null && (
                        <ControlDelHilo
                            accountId={data.accountId}
                            conversationId={data.conversationId}
                            mando={data.mando}
                            onHecho={() => void refetch()}
                        />
                    )}
                </section>
            </div>
        </Marco>
    )
}

/**
 * `volverA` es la consola DEL NEGOCIO, no la portada (FR-037): la ficha cuelga de
 * un negocio, así que volver a la portada haría perder el contexto en el que se
 * estaba trabajando.
 */
function Marco({ volverA, children }: { volverA: string; children: React.ReactNode }) {
    return (
        <div className="space-y-4">
            <Link to={volverA} className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-300">
                ← Volver a la lista
            </Link>
            {children}
        </div>
    )
}

function Titulo({ children }: { children: React.ReactNode }) {
    return <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">{children}</h2>
}

function Cabecera({ d }: { d: DetalleConversacion }) {
    return (
        <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-neutral-800 pb-3">
            <span className="text-base font-semibold text-neutral-100">#{d.conversationId}</span>
            {d.synthetic && <Badge tone="info">test</Badge>}
            <span className="text-neutral-400">{d.businessName ?? (d.synthetic ? '—' : 'negocio desconocido')}</span>
            <OutcomeBadge outcome={d.outcome} />
            {d.outcomeReason && <span className="text-xs text-neutral-600">{d.outcomeReason}</span>}
            <div className="ml-auto flex items-center gap-3 text-xs">
                {d.chatwootUrl && (
                    <a href={d.chatwootUrl} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">
                        Abrir en Chatwoot ↗
                    </a>
                )}
                {d.langfuseUrl && (
                    <a href={d.langfuseUrl} target="_blank" rel="noreferrer" className="text-sky-400 hover:underline">
                        Traza LLM ↗
                    </a>
                )}
            </div>
        </header>
    )
}

const ETIQUETA_EVENTO: Record<string, string> = {
    APPOINTMENT_CREATED: 'Cita creada',
    APPOINTMENT_MODIFIED: 'Cita modificada',
    APPOINTMENT_CANCELLED: 'Cita cancelada',
    APPOINTMENT_REVERTED: 'Cita revertida',
    HOLD_ACQUIRED: 'Hueco reservado',
    HOLD_RELEASED: 'Hueco liberado',
    SLOT_COLLISION_CHECK: 'Comprobación de hueco',
    GDPR_CONSENT_GRANTED: 'Consentimiento RGPD',
    NEW_USER_REGISTERED: 'Alta de usuario',
}

function Resultado({ audit }: { audit: EventoAuditoriaVista[] }) {
    if (audit.length === 0) return <p className="text-sm text-neutral-600">Sin eventos de negocio registrados.</p>
    return (
        <ul className="space-y-1">
            {audit.map((a, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-neutral-200">{ETIQUETA_EVENTO[a.event] ?? a.event}</span>
                    {a.slot?.['fecha'] && (
                        <span className="text-neutral-400">
                            {a.slot['fecha']}
                            {a.slot['hora'] ? ` · ${a.slot['hora']}` : ''}
                        </span>
                    )}
                    {a.outcome && <Badge tone={a.outcome === 'OK' || a.outcome === 'FREE' ? 'success' : 'neutral'}>{a.outcome}</Badge>}
                    {a.at && <span className="ml-auto font-mono text-xs text-neutral-600">{fechaHora(a.at)}</span>}
                </li>
            ))}
        </ul>
    )
}
